import { createSlice } from '@reduxjs/toolkit'
import { register } from '../../../../helpers/reducerRegistry'
import {
  getBreakdownCompareThunk,
  getBreakdownThunk,
  getCommentaryThunk,
  getDistributionScoreThunk,
  getHistoricalChangeDrdDataThunk,
  getHistoricalChangeThunk,
  getPDTermStructureThunk,
  getRiskDriverCompareThunk,
  getRiskDriverDataThunk,
  getRiskScaleInterpretationThunk,
} from './thunk'

const initialState = {
  loading: {
    riskScaleInterpretation: true,
    historicalChange: false,
    listFilterTimeHistoricalChange: true,
    listPDTermStructure: true,
    commentary: true,
    linkageScore: true,
    distributionScore: true,
    breakdown: true,
    riskDriver: true,
  },
  riskScaleInterpretation: [],
  chartHistoricalChange: [],
  listFilterTimeHistoricalChange: [],
  historicalChangeTable: {},
  listPDTermStructure: {},
  commentary: {},
  distributionScore: [],
  distributionScoreDes: {},
  breakdown: [],
  breakdownCompare: {
    baseData: [],
    compareData: [],
  },
  //RiskDriversTable
  riskDriversIds: [],
  riskDriversData: {},
  relativeContributions: [],
  riskDriversIsAdjust: false,
  riskDriversNoteAdjust: '',
}
const slice = createSlice({
  name: 'score/corporateRiskScore',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      getRiskScaleInterpretationThunk.pending,
      (state, action) => {
        state.loading.riskScaleInterpretation = true
      },
    )
    builder.addCase(
      getRiskScaleInterpretationThunk.fulfilled,
      (state, action) => {
        state.riskScaleInterpretation = action.payload
        state.loading.riskScaleInterpretation = false
      },
    )
    builder.addCase(
      getRiskScaleInterpretationThunk.rejected,
      (state, action) => {
        state.loading.riskScaleInterpretation = action.payload.loading
      },
    )

    // getHistoricalChangeDrdDataThunk
    builder.addCase(
      getHistoricalChangeDrdDataThunk.pending,
      (state, action) => {
        state.loading.listFilterTimeHistoricalChange = true
      },
    )
    builder.addCase(
      getHistoricalChangeDrdDataThunk.fulfilled,
      (state, action) => {
        state.listFilterTimeHistoricalChange = action.payload
        state.loading.listFilterTimeHistoricalChange = false
      },
    )
    builder.addCase(
      getHistoricalChangeDrdDataThunk.rejected,
      (state, action) => {
        state.loading.listFilterTimeHistoricalChange = action.payload.loading
      },
    )

    // chartHistoricalChange
    builder.addCase(getHistoricalChangeThunk.pending, (state, action) => {
      state.loading.historicalChange = true
    })
    builder.addCase(getHistoricalChangeThunk.fulfilled, (state, action) => {
      state.chartHistoricalChange = action.payload
      const probabilityOfDefault = {
        indicator: 'corporateRiskScore.probabilityOfDefault',
        unit: '%',
        ...Object.fromEntries(
          action.payload.map((item) => [
            [item.intYear, item.intMonth, item.intQuarter].join('-'),
            (item.pd * 100).toFixed(2) + '%',
          ]),
        ),
      }
      const companyScore = {
        indicator: 'corporateRiskScore.scoreCompany',
        unit: 'corporateRiskScore.point',
        ...Object.fromEntries(
          action.payload.map((item) => [
            [item.intYear, item.intMonth, item.intQuarter].join('-'),
            item.companyScore,
          ]),
        ),
      }
      const modelSegmentScore = {
        indicator: 'corporateRiskScore.scoreModelSegment',
        unit: 'corporateRiskScore.point',
        ...Object.fromEntries(
          action.payload.map((item) => [
            [item.intYear, item.intMonth, item.intQuarter].join('-'),
            item.modelSegmentScore,
          ]),
        ),
      }
      state.historicalChangeTable = {
        probabilityOfDefault,
        companyScore,
        modelSegmentScore,
      }
      state.loading.historicalChange = false
    })
    builder.addCase(getHistoricalChangeThunk.rejected, (state, action) => {
      state.loading.historicalChange = action.payload.loading
    })

    builder.addCase(getPDTermStructureThunk.pending, (state, action) => {
      state.loading.listPDTermStructure = true
    })
    builder.addCase(getPDTermStructureThunk.fulfilled, (state, action) => {
      state.listPDTermStructure = action.payload || {}
      state.loading.listPDTermStructure = false
    })
    builder.addCase(getPDTermStructureThunk.rejected, (state, action) => {
      state.loading.listPDTermStructure = action.payload.loading
    })

    builder.addCase(getCommentaryThunk.pending, (state, action) => {
      state.loading.commentary = true
    })
    builder.addCase(getCommentaryThunk.fulfilled, (state, action) => {
      state.commentary = action.payload
      state.loading.commentary = false
    })
    builder.addCase(getCommentaryThunk.rejected, (state, action) => {
      state.loading.commentary = action.payload.loading
    })

    builder.addCase(getDistributionScoreThunk.pending, (state, action) => {
      state.loading.distributionScore = true
    })
    builder.addCase(getDistributionScoreThunk.fulfilled, (state, action) => {
      const mapKey = {
        1: 'modelSegment',
        2: 'industry',
        3: 'companySize',
      }
      const { data, industryName, companySizeName, modelSegmentName } =
        action.payload
      const groupedByOrderSort = data.reduce((acc, item) => {
        const key = mapKey[item.orderSort]
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(item)
        return acc
      }, {})
      state.distributionScore = groupedByOrderSort
      state.distributionScoreDes = {
        industryName: industryName,
        companySizeName: companySizeName,
        modelSegmentName: modelSegmentName,
      }
      state.loading.distributionScore = false
    })
    builder.addCase(getDistributionScoreThunk.rejected, (state, action) => {
      state.loading.distributionScore = action.payload.loading
    })

    builder.addCase(getBreakdownThunk.pending, (state, action) => {
      state.loading.breakdown = true
    })
    builder.addCase(getBreakdownThunk.fulfilled, (state, action) => {
      state.breakdown = action.payload
      state.loading.breakdown = false
    })
    builder.addCase(getBreakdownThunk.rejected, (state, action) => {
      state.loading.breakdown = action.payload.loading
    })

    builder.addCase(getBreakdownCompareThunk.pending, (state, action) => {
      state.loading.breakdown = true
    })
    builder.addCase(getBreakdownCompareThunk.fulfilled, (state, action) => {
      state.breakdownCompare = action.payload
      state.loading.breakdown = false
    })
    builder.addCase(getBreakdownCompareThunk.rejected, (state, action) => {
      state.loading.breakdown = action.payload.loading
    })

    builder.addCase(getRiskDriverDataThunk.pending, (state, action) => {
      state.loading.riskDriver = true
      state.riskDriversIds = []
      state.riskDriversData = {}
      state.relativeContributions = []
      state.riskDriversIsAdjust = false
      state.riskDriversNoteAdjust = ''
    })
    builder.addCase(getRiskDriverDataThunk.fulfilled, (state, action) => {
      const newData = action.payload
      const {
        financialRisk,
        industryRick,
        organizationRisk,
        isAdjust,
        noteAdjust,
      } = newData || {}
      const ids = []
      const data = {}
      Object.entries(newData)
        .filter(([groupKey, arr]) => Array.isArray(arr) && arr.length > 0)
        .forEach(([groupKey, arr]) => {
          const groupId = `group__${groupKey}`
          ids.push(groupId)
          data[groupId] = {
            isGroup: true,
            parentRiskDriverName: arr[0].parentRiskDriverName || groupKey,
            parentRiskDriverId: arr[0].parentRiskDriverId,
            groupKey,
            riskDriverName: arr[0].parentRiskDriverName || groupKey,
          }
          arr.forEach((item, idx) => {
            const id = `${groupKey}_${item.riskDriverId}`
            ids.push(id)
            data[id] = {
              ...item,
              groupKey,
              id,
              stt: idx + 1,
              parentRiskDriverName: arr[0].parentRiskDriverName || groupKey,
              parentRiskDriverId: arr[0].parentRiskDriverId,
            }
          })
        })
      state.riskDriversIds = ids
      state.riskDriversData = data
      state.relativeContributions = [
        ...financialRisk,
        ...organizationRisk,
        ...industryRick,
      ].filter((item) => !!item.relative)
      state.riskDriversIsAdjust = isAdjust
      state.riskDriversNoteAdjust = noteAdjust
      state.loading.riskDriver = false
    })
    builder.addCase(getRiskDriverDataThunk.rejected, (state, action) => {
      state.loading.riskDriver = action.payload.loading
    })

    builder.addCase(getRiskDriverCompareThunk.pending, (state, action) => {
      state.loading.riskDriver = true
      state.riskDriversIds = []
      state.riskDriversData = {}
      state.relativeContributions = []
      state.riskDriversIsAdjust = false
      state.riskDriversNoteAdjust = ''
    })
    builder.addCase(getRiskDriverCompareThunk.fulfilled, (state, action) => {
      const ids = []
      const data = {}
      const { baseData = {}, compareData = {} } = action.payload || {}
      const newBaseData = filterRiskDriverData(baseData)
      const newCompareData = filterRiskDriverData(compareData)

      const { isAdjust, noteAdjust } = newBaseData || {}
      const { financialRisk, industryRick, organizationRisk } =
        newCompareData || {}

      Object.entries(newBaseData)
        .filter(([groupKey, arr]) => Array.isArray(arr) && arr.length > 0)
        .forEach(([groupKey, arr]) => {
          const groupId = `group__${groupKey}`
          ids.push(groupId)
          data[groupId] = {
            isGroup: true,
            parentRiskDriverName: arr[0].parentRiskDriverName || groupKey,
            parentRiskDriverId: arr[0].parentRiskDriverId,
            groupKey,
            riskDriverName: arr[0].parentRiskDriverName || groupKey,
          }
          arr.forEach((item, idx) => {
            const id = `${groupKey}_${item.riskDriverId}`
            // Tìm item compare tương ứng
            const compareArr = compareData[groupKey] || []
            const compareItem = compareArr.find(
              (c) => c.riskDriverId === item.riskDriverId,
            )
            data[id] = {
              ...item,
              compare: compareItem || null,
              groupKey,
              id,
              stt: idx + 1,
              parentRiskDriverName: arr[0].parentRiskDriverName || groupKey,
              parentRiskDriverId: arr[0].parentRiskDriverId,
            }
            ids.push(id)
          })
        })
      state.riskDriversIsAdjust = isAdjust
      state.riskDriversNoteAdjust = noteAdjust
      state.riskDriversIds = ids
      state.riskDriversData = data
      state.relativeContributions = [
        ...financialRisk,
        ...organizationRisk,
        ...industryRick,
      ].filter((item) => !!item.relative)

      state.loading.riskDriver = false
    })
    builder.addCase(getRiskDriverCompareThunk.rejected, (state, action) => {
      state.loading.riskDriver = action.payload.loading
    })
  },
})

const FIELDS_WITH_GROWTH = ['scoreValue', 'contribution', 'median', 'relative']

export const selectLoading = (type) => (state) => {
  const loadingObj = state[slice.name].loading
  if (!type) return Object.values(loadingObj).some(Boolean)
  if (Array.isArray(type)) return type.some((i) => loadingObj[i])
  return loadingObj[type]
}
export const selectLoadingGetRiskScaleInterpretation = (state) =>
  state[slice.name].loading.riskScaleInterpretation
export const selectRiskScaleInterpretation = (state) =>
  state[slice.name].riskScaleInterpretation
export const selectChartHistoricalChange = (state) =>
  state[slice.name].chartHistoricalChange
export const selectListFilterTimeHistoricalChange = (state) =>
  state[slice.name].listFilterTimeHistoricalChange
export const selectHistoricalChangeTable = (rowId, colId) => (state) => {
  return state[slice.name].historicalChangeTable?.[rowId]?.[colId] ?? '-'
}
export const selectListPDTermStructure = (state) =>
  state[slice.name].listPDTermStructure
export const selectCommentary = (state) => state[slice.name].commentary
export const selectDistributionScore = (state) => {
  return state[slice.name].distributionScore
}
export const selectDistributionScoreDes = (state) => {
  return state[slice.name].distributionScoreDes
}
export const selectBreakdown = (state) => {
  return state[slice.name].breakdown
}
export const selectBreakdownCompare = (state) => {
  return state[slice.name].breakdownCompare
}
export const selectRiskDriversIds = (state) => {
  return state[slice.name].riskDriversIds
}
export const selectRiskDriversById = (rowId, colId) => (state) => {
  return state[slice.name].riskDriversData[rowId][colId]
}
export const selectRelativeContributions = (state) => {
  return state[slice.name].relativeContributions
}
export const selectRiskDriversIsAdjust = (state) => {
  return state[slice.name].riskDriversIsAdjust
}
export const selectRiskDriversNoteAdjust = (state) => {
  return state[slice.name].riskDriversNoteAdjust
}

export const selectRiskDriversByIdWithCompare = (id, field) => (state) => {
  const row = state[slice.name].riskDriversData[id]
  if (!row) return null

  if (row.isGroup) {
    return row[field]
  }

  if (FIELDS_WITH_GROWTH.includes(field)) {
    const baseValue = row[field]
    const valueCompare = row.compare ? row.compare[field] : null
    return {
      value: baseValue,
      valueCompare,
    }
  }

  return row[field]
}

export const { changePeriodType } = slice.actions

register(slice.name, slice.reducer)

const filterRiskDriverData = (obj) => {
  const keyTable = ['scoreValue', 'contribution', 'median', 'relative']
  const newObj = {}
  for (const [key, arr] of Object.entries(obj)) {
    if (Array.isArray(arr)) {
      newObj[key] = arr.filter((item) =>
        keyTable.some((k) => item[k] !== null && item[k] !== undefined),
      )
    } else {
      newObj[key] = arr
    }
  }
  return newObj
}
